import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { auth } from './firebase';
import API_URL from './config';
import AppLoading from './components/AppLoading';
import APP_VERSION from './version';
import queryClient from './queryClient';

import { Permission } from './permissions';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// ── Cached player status ─────────────────────────────────────────────────
// Home's whole featured card — "Resume Scoring" vs "Play Golf" vs "View Tee
// Sheet" — is derived from playerStatus, so it is seeded from the last known
// answer to make the page appear instantly. Two rules keep that from making it
// appear WRONG:
//
//   · it is keyed by SOCIETY. A single shared key meant switching society
//     showed the other society's round status until the fetch landed — an
//     active round that belonged to someone else's day.
//   · it is stamped, and only trusted for a few hours. A round is a thing that
//     happens on a day: yesterday's "active round" is not a useful guess about
//     this morning, and showing it means the CTA visibly changes under the
//     user's finger once /me/status answers.
//
// Past either rule Home simply shows 'loading' until the server answers, which
// is honest and brief.
const STATUS_CACHE_TTL = 6 * 60 * 60 * 1000;   // 6 hours
const statusCacheKey = (societyId) => `cached_player_status_${societyId}`;

function currentSocietyIdFromStorage() {
  return localStorage.getItem('preferred_society_id');
}

// A society a SYSTEM ADMIN has opened without being a member of it.
//
// It cannot live in `userProfile.societies` — that list is the user's
// memberships, and a support account has none — so it is persisted separately
// and wins over the membership list while it is set. Without this the society
// survived exactly until the next /auth/me, which re-seeds currentSociety from
// the memberships and would drop them straight back out.
const ADMIN_SOCIETY_KEY = 'admin_society';

function readAdminSociety() {
  try {
    const raw = localStorage.getItem(ADMIN_SOCIETY_KEY);
    const s = raw ? JSON.parse(raw) : null;
    return s && s.id ? s : null;
  } catch { return null; }
}

function readCachedStatus(societyId) {
  if (!societyId) return { status: 'loading' };
  try {
    const cached = JSON.parse(localStorage.getItem(statusCacheKey(societyId)) || 'null');
    if (!cached || !cached.at || !cached.status) return { status: 'loading' };
    if (Date.now() - cached.at > STATUS_CACHE_TTL) return { status: 'loading' };
    return cached.status;
  } catch { return { status: 'loading' }; }
}

function writeCachedStatus(societyId, status) {
  if (!societyId) return;
  try {
    localStorage.setItem(statusCacheKey(societyId), JSON.stringify({ at: Date.now(), status }));
  } catch { /* quota or private mode — the cache is an optimisation */ }
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [userProfile, setUserProfile] = useState(() => {
    const saved = localStorage.getItem('cached_user_profile');
    try { return saved ? JSON.parse(saved) : null; } catch (e) { return null; }
  });
  // If we have a cached profile, don't block rendering — show cached UI instantly
  const hasCachedProfile = userProfile !== null;
  const [loading, setLoading] = useState(!hasCachedProfile);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const firstAuthAnswerRef = useRef(false);
  // The profile fetch the auth listener started for the current user, so the
  // login screen can wait on THAT fetch rather than racing a second one.
  const profilePromiseRef = useRef(null);
  const waitForProfile = useCallback(
    () => profilePromiseRef.current || Promise.resolve(null), []);
  // An open admin view outranks the membership list on first paint too —
  // otherwise a reload drops a system admin back into their own society.
  const [adminSociety, setAdminSociety] = useState(readAdminSociety);
  const [currentSociety, setCurrentSociety] = useState(() => {
    const adminView = readAdminSociety();
    if (adminView) return adminView;
    const savedId = localStorage.getItem('preferred_society_id');
    const savedProfile = localStorage.getItem('cached_user_profile');
    if (savedId && savedProfile) {
      try {
        const profile = JSON.parse(savedProfile);
        return profile.societies?.find(s => s.id.toString() === savedId) || profile.societies?.[0] || null;
      } catch (e) { return null; }
    }
    return null;
  });
  const [archivedSocieties, setArchivedSocieties] = useState([]);
  const [dismissedArchiveIds, setDismissedArchiveIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissed_archive_ids') || '[]'); } catch { return []; }
  });

  const [playerStatus, setPlayerStatus] = useState(
    () => readCachedStatus(currentSocietyIdFromStorage()));

  // Fetch player status (Active Round, Upcoming Roll Up, etc.)
  const fetchPlayerStatus = async (societyId) => {
    // "Not signed in yet" is not the same answer as "nothing on today". Home is
    // un-gated, so it mounts and calls refreshPlayerStatus() while Firebase is
    // still restoring the session — and this used to answer that with 'idle',
    // throwing away the cached status the provider had just read. The effect
    // below re-runs on `currentUser`, so the right move is to say nothing and
    // let the real fetch happen a moment later.
    if (!currentUser) return;
    if (!societyId) {
      setPlayerStatus({ status: 'idle' });
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/me/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Society-ID': societyId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlayerStatus(data);
        writeCachedStatus(societyId, data);
        // Returned too, for a caller that must act on the FRESH answer rather
        // than wait a render for the state (the wizard routing a just-started
        // round to its card).
        return data;
      } else {
        // A FAILED REQUEST IS NOT AN ANSWER. This used to set 'idle' and write
        // that to the cache — so a 401 during a token refresh, a 429, or a 502
        // while the backend restarted after a deploy did not just blank Home
        // for a moment: it poisoned the six-hour status cache, and every load
        // after it painted "no active round" from that until a good fetch
        // happened to land. Intermittent, sticky, and impossible to reproduce
        // on demand. "You have nothing on" arrives as a 200; anything else is
        // the server failing to say, and the last known answer is the better
        // guess. This is what the catch below has always done — the two paths
        // now agree.
        console.warn(`/me/status ${response.status} — keeping the last known status`);
      }
    } catch (error) {
      // Network failure (e.g. offline). Do NOT reset to 'idle' — that would
      // wipe the cached active-round status and can cascade into clearing the
      // in-progress offline scoring session. Keep whatever was last known.
      console.error("Error fetching player status:", error);
    }
  };

  useEffect(() => {
    if (currentSociety) {
      // Swap to THIS society's own cached answer before asking the server.
      // Without it the previous society's status stayed on screen for the
      // length of the request, which is where "Resume Scoring" for a round in
      // another society came from.
      setPlayerStatus(readCachedStatus(currentSociety.id));
      fetchPlayerStatus(currentSociety.id);
    } else {
      setPlayerStatus({ status: 'idle' });
    }
    // fetchPlayerStatus is stable for these inputs; re-run only on society/user change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSociety, currentUser]);

  // Wrap setCurrentSociety to persist to localStorage. Picking a society the
  // normal way ends any admin view — you are back in your own societies.
  const changeSociety = (society) => {
    if (society) {
      localStorage.setItem('preferred_society_id', society.id);
    }
    localStorage.removeItem(ADMIN_SOCIETY_KEY);
    setAdminSociety(null);
    setCurrentSociety(society);
  };

  /* Open a society as a SYSTEM ADMIN, without joining it.
     Joining would be the wrong tool: it would put a support account on the tee
     sheet, in the member list and in the Order of Merit. The server side of
     this is `society_required`, which admits a system admin with no membership
     row; here we just have to hold a society object that is not in
     `userProfile.societies` and keep it through the next profile refresh. */
  const enterSocietyAsAdmin = (society) => {
    if (!society?.id) return;
    try {
      localStorage.setItem(ADMIN_SOCIETY_KEY, JSON.stringify(society));
    } catch { /* private mode — the view still works for this session */ }
    localStorage.setItem('preferred_society_id', society.id);
    setAdminSociety(society);
    setCurrentSociety(society);
  };

  // Back to the admin's own societies (or none, which is the usual case for a
  // support account and is what the join/create prompts are for).
  const exitAdminSociety = () => {
    localStorage.removeItem(ADMIN_SOCIETY_KEY);
    setAdminSociety(null);
    const own = userProfile?.societies?.[0] || null;
    if (own) localStorage.setItem('preferred_society_id', own.id);
    else localStorage.removeItem('preferred_society_id');
    setCurrentSociety(own);
  };

  // Sign up
  const signup = async (email, password, name) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    if (name) {
      await updateProfile(userCredential.user, { displayName: name });
    }
    return userCredential;
  };

  // Login
  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const clearUserState = () => {
    localStorage.removeItem('cached_user_profile');
    localStorage.removeItem('dismissed_archive_ids');
    localStorage.removeItem('preferred_society_id');
    localStorage.removeItem(ADMIN_SOCIETY_KEY);
    localStorage.removeItem('cached_last_round');
    // Player status and last-round snapshots are per society (and per player),
    // so they're cleared by prefix rather than by name — the next user on this
    // device must not inherit the last one's round.
    Object.keys(localStorage)
      .filter(k => k.startsWith('cached_player_status')
        || k.startsWith('cached_last_round_') || k.startsWith('cached_lb_')
        // Society-wide in name only — it holds who else is out (with the
        // viewer's own round removed) and an activity row that says "You".
        || k.startsWith('cached_home_feed_')
        // A half-typed course is the previous user's work, not this device's.
        || k.startsWith('course_draft_')
        // Nor a half-set-up quick round (utils/quickRoundDraft).
        || k.startsWith('quick_round_draft_'))
      .forEach(k => localStorage.removeItem(k));
    setUserProfile(null);
    setCurrentSociety(null);
    setAdminSociety(null);
    setArchivedSocieties([]);
    setDismissedArchiveIds([]);
    setPlayerStatus({ status: 'loading' });
    queryClient.clear();
  };

  // Logout
  const logout = () => {
    clearUserState();
    return signOut(auth);
  };

  // Reset Password
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Check if current user has a specific permission in the current society
  const hasPermission = (permission) => {
    if (!userProfile || !currentSociety) return false;
    
    // System Admins always have permission
    if (userProfile.is_system_admin) return true;

    // Find the current society in the user's society list to get permissions
    // Use loose equality (==) to handle string/number ID mismatches
    const societyData = userProfile.societies?.find(s => String(s.id) === String(currentSociety.id));
    if (!societyData) return false;

    // If permissions array is missing (stale cache), deny and force profile refresh
    if (!societyData.permissions) return false;

    return societyData.permissions.includes(permission);
  };

  // Sync Firebase user with our Backend User Profile
  const fetchUserProfile = async (firebaseUser) => {
    setIsProfileLoading(true);
    try {
      const token = await firebaseUser.getIdToken();
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      // Which client this is, for the admin's login history
      // (services/client_platform.py). A query string rather than headers: a
      // new header has to be allowed by the API's CORS first, and the website
      // and the server deploy at the same moment.
      const client = new URLSearchParams({ platform: Capacitor.getPlatform(), build: APP_VERSION });

      const response = await fetch(`${API_URL}/auth/me?${client}`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        const fullProfile = { ...data.user, societies: data.societies };
        setArchivedSocieties(data.archived_societies || []);
        // Cache the profile for next time
        localStorage.setItem('cached_user_profile', JSON.stringify(fullProfile));
        
        // Merge societies into the user profile object so components can access them
        setUserProfile(fullProfile);
        
        // An open admin view is a deliberate choice about a society that is NOT
        // in this list, so it survives the refresh. Without this guard every
        // /auth/me — one lands on each app open — silently threw the admin back
        // into their own first society mid-task.
        const adminView = readAdminSociety();
        if (adminView) {
          setCurrentSociety(adminView);
          return data;
        }

        // If user has societies, select the preferred one if it exists, otherwise default to first
        if (data.societies && data.societies.length > 0) {
          const storedSocietyId = localStorage.getItem('preferred_society_id');
          
          // Always try to find the "current" society in the fresh data to ensure we have latest permissions
          const freshSociety = data.societies.find(s => String(s.id) === String(storedSocietyId)) 
                             || data.societies.find(s => String(s.id) === String(currentSociety?.id))
                             || data.societies[0];
          
          if (freshSociety) {
            // Update current society with fresh data (including new permissions array)
            setCurrentSociety(freshSociety);
            // Also ensure the ID is persisted if it's the first time or changed
            localStorage.setItem('preferred_society_id', freshSociety.id);
          }
        }
        
        return data;
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setIsProfileLoading(false);
    }
    return null;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Only the FIRST answer may hold the app back. That is a cold start, where
      // there is nothing to show yet anyway. A later one is somebody signing in
      // or up from the login screen — and hiding the tree then unmounted that
      // screen mid-submit: the app went blank, the form came back EMPTY with its
      // button live, and the subscription provider restarted RevenueCat. The
      // login screen keeps itself up instead and waits on `waitForProfile()`.
      const isFirstAnswer = !firstAuthAnswerRef.current;
      firstAuthAnswerRef.current = true;
      setAuthInitialized(true);
      setCurrentUser(user);
      if (user) {
        const hasCached = !!localStorage.getItem('cached_user_profile');
        const profile = fetchUserProfile(user);
        profilePromiseRef.current = profile;
        if (hasCached || !isFirstAnswer) {
          // Cached profile, or a sign-in the login screen is already covering —
          // render on, and let the profile arrive.
          setLoading(false);
        } else {
          // Cold start with no cached profile — hold until the profile is ready.
          setLoading(true);
          await profile;
          setLoading(false);
        }
      } else {
        profilePromiseRef.current = null;
        clearUserState();
        setIsProfileLoading(false);
        setLoading(false);
      }
    });

    return unsubscribe;
    // Mount-only: set up the auth-state listener once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSocietyOwner = !!(userProfile && currentSociety && currentSociety.owner_id === userProfile.id);

  const canCreateRound = isSocietyOwner || hasPermission(Permission.CREATE_TOURNAMENT) || hasPermission(Permission.CREATE_QUICK_ROUND);

  const dismissArchiveNotice = (societyId) => {
    const updated = [...dismissedArchiveIds, societyId];
    setDismissedArchiveIds(updated);
    localStorage.setItem('dismissed_archive_ids', JSON.stringify(updated));
  };

  const pendingArchiveNotices = archivedSocieties.filter(s => !dismissedArchiveIds.includes(s.id));

  const value = {
    currentUser,
    authInitialized,
    userProfile,
    isProfileLoading,
    currentSociety,
    playerStatus,
    refreshPlayerStatus: () => currentSociety && fetchPlayerStatus(currentSociety.id),
    setCurrentSociety: changeSociety,
    // Support access to a society this user is not a member of.
    isViewingAsAdmin: !!adminSociety,
    adminSociety,
    enterSocietyAsAdmin,
    exitAdminSociety,
    signup,
    login,
    logout,
    resetPassword,
    fetchUserProfile,
    waitForProfile,
    hasPermission,
    isSocietyOwner,
    canCreateRound,
    archivedSocieties,
    pendingArchiveNotices,
    dismissArchiveNotice,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <AppLoading /> : children}
    </AuthContext.Provider>
  );
};
